import { Modal, Tabs } from 'antd';
import React, { FC, useState } from 'react';
import styles from './findLocationModal.module.scss';
import { FieldArray, Form, Formik } from 'formik';
import clsx from 'clsx';
import Map from '../map';
import InputField from '../InputField';
import { validationSchema } from './validationSchema';
import COButton from '../COButton';
import { Address } from 'models/Address/address.model';
import TabPane from 'antd/lib/tabs/TabPane';
import { RawMaterialProps } from 'enums/RawMaterialEnums';
import WarningIcon from 'assets/images/error_warning.svg';
import { Origin } from 'views/CreateProductFlow/CreateProductFlowInterfaces';
import { modalCloseIcon } from '../OrganizationModal';
import {
  searchPlaceByCountryProvince,
  setAddressInState,
} from './locationModal.util';

interface FindLocationModalProps {
  showModal: boolean;
  onSave: (data: Origin[]) => void;
  onCancel: () => void;
  initialData?: Address | undefined;
  fetchInitialData?: () => Address | undefined;
  selectedIndexData?: Origin[];
  activeTabIndex?: number;
  addCallBack?: () => void;
}

const FindLocationModal: FC<FindLocationModalProps> = props => {
  const {
    showModal,
    onSave,
    onCancel,
    selectedIndexData,
    activeTabIndex,
    addCallBack,
  } = props;
  const [activeKey, setActiveKey] = useState(activeTabIndex?.toString() ?? '0');
  const handleChange = (key: string) => {
    setActiveKey(key);
  };

  const initialValues = selectedIndexData ?? [];
  const [fetchingAddress, setFetchingAddress] = useState<boolean>(false);

  const handleSave = (data: { address: Origin[] }) => {
    onSave(data.address);
    setActiveKey('0');
  };

  return (
    <Modal
      destroyOnClose={true}
      maskClosable={false}
      width={725}
      footer={null}
      wrapClassName={styles.modalContainer}
      open={showModal}
      closeIcon={modalCloseIcon}
      onCancel={onCancel}>
      <div className={styles.formContainer}>
        <div className={styles.headerContainer}>
          <h2>Find Location</h2>
          <Formik
            enableReinitialize
            initialValues={{
              address: initialValues,
            }}
            onSubmit={handleSave}
            validationSchema={validationSchema}>
            {({ setFieldValue, values, errors }) => (
              <Form>
                <FieldArray
                  name={`${RawMaterialProps.ADDRESS}`}
                  render={arrayHelpers => (
                    <div className={styles.findLocationTab}>
                      <Tabs
                        activeKey={activeKey}
                        onChange={handleChange}
                        type="editable-card"
                        onEdit={(index, action) => {
                          if (action === 'remove') {
                            arrayHelpers.remove(Number(index));
                            setActiveKey('0');
                          }
                        }}
                        addIcon={
                          <span
                            className={styles.addOriginBtn}
                            onClick={() => {
                              arrayHelpers.push({
                                id: undefined,
                                ingredientOrigin: undefined,
                                bestfitRegion: undefined,
                                originShare: undefined,
                                percentage: '%',
                                address: new Address(),
                                share: null,
                                addressLabel: null,
                                activityMappingRegionSpecId: null,
                              });
                              addCallBack?.();

                              const originLength =
                                arrayHelpers.form.values.address.length;
                              setActiveKey(originLength?.toString());
                            }}>
                            +
                            {arrayHelpers?.form?.values?.address?.length > 3
                              ? ''
                              : 'Add Origin'}
                          </span>
                        }>
                        {values?.address?.map((address, index) => (
                          <>
                            <TabPane
                              tab={
                                <>
                                  <div className={styles.marginExtra}>
                                    <span
                                      className={clsx({
                                        [styles.tabLabel]: true,
                                        [styles.active]:
                                          activeKey === String(index),
                                        [styles.inactive]:
                                          activeKey !== String(index),
                                      })}>
                                      {errors.address?.[index] && (
                                        <img
                                          src={WarningIcon}
                                          className={styles.warningIcon}
                                        />
                                      )}
                                      {`Origin ${index + 1}`}
                                    </span>
                                  </div>
                                </>
                              }
                              closable={values.address.length !== 1}
                              key={index}>
                              <div className={styles.fieldContainer}>
                                <div
                                  className={clsx(
                                    styles.inputContainer,
                                    styles.longContainer
                                  )}>
                                  <div className={styles.mapContainer}>
                                    <Map
                                      name={`${RawMaterialProps.ADDRESS}.${index}.${RawMaterialProps.MAP}`}
                                      setAddress={(
                                        addressComponents: google.maps.GeocoderAddressComponent[],
                                        latling: google.maps.LatLng | null
                                      ) =>
                                        setAddressInState(
                                          addressComponents,
                                          latling,
                                          setFieldValue,
                                          index
                                        )
                                      }
                                      latitude={address?.address?.latitude}
                                      longitude={address?.address?.longitude}
                                      focusSearchInputOnReady={showModal}
                                    />
                                  </div>
                                </div>
                                <div className={styles.inputContainer}>
                                  <InputField
                                    name={`${RawMaterialProps.ADDRESS}.${index}.${RawMaterialProps.ADDRESS}.${RawMaterialProps.STREET}`}
                                    placeholderstring="Street, Locality"
                                    label=""
                                  />
                                </div>
                                <div className={styles.inputContainer}>
                                  <InputField
                                    name={`${RawMaterialProps.ADDRESS}.${index}.${RawMaterialProps.ADDRESS}.${RawMaterialProps.CITY}`}
                                    placeholderstring="City"
                                    label=""
                                  />
                                </div>
                                <div className={styles.inputContainer}>
                                  <InputField
                                    name={`${RawMaterialProps.ADDRESS}.${index}.${RawMaterialProps.ADDRESS}.${RawMaterialProps.PROVINCE}`}
                                    placeholderstring="Province"
                                    label=""
                                    onBlur={() =>
                                      searchPlaceByCountryProvince(
                                        address?.address?.country ?? '',
                                        address?.address?.province ?? '',
                                        setFieldValue,
                                        index,
                                        setFetchingAddress
                                      )
                                    }
                                  />
                                </div>
                                <div className={styles.inputContainer}>
                                  <InputField
                                    name={`${RawMaterialProps.ADDRESS}.${index}.${RawMaterialProps.ADDRESS}.${RawMaterialProps.COUNTRY}`}
                                    placeholderstring="Country"
                                    label=""
                                    onBlur={() =>
                                      searchPlaceByCountryProvince(
                                        address?.address?.country ?? '',
                                        address?.address?.province ?? '',
                                        setFieldValue,
                                        index,
                                        setFetchingAddress
                                      )
                                    }
                                  />
                                </div>
                                <div className={styles.inputContainer}>
                                  <InputField
                                    name={`${RawMaterialProps.ADDRESS}.${index}.${RawMaterialProps.ADDRESS}.${RawMaterialProps.POSTAL_CODE}`}
                                    placeholderstring="Postal Code"
                                    label=""
                                  />
                                </div>
                                <div className={styles.inputContainer}>
                                  <InputField
                                    name={`postal_code_${index}`}
                                    placeholderstring="Postal Code"
                                    label=""
                                    className="d-none"
                                  />
                                </div>
                              </div>

                              <div className={styles.buttonContainer}>
                                <div className={styles.singleButtonContainer}>
                                  <COButton submit loading={fetchingAddress}>
                                    Done
                                  </COButton>
                                  <COButton secondary onClick={onCancel}>
                                    Cancel
                                  </COButton>
                                </div>
                              </div>
                            </TabPane>
                          </>
                        ))}
                      </Tabs>
                    </div>
                  )}
                />
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
  );
};

export default FindLocationModal;
